import {
  Alert,
  BusinessIcon,
  Button,
  CalendarIcon,
  Dialog,
  DocumentIcon,
  EmptyState,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  OpenInNewIcon,
  SearchIcon,
  CloseIcon,
  Skeleton,
  Stack,
  Tag,
  TextField,
  TopicIcon,
  Typography,
} from '@all-manager/design-system-v2';
import type { ReactNode } from 'react';
import { SearchItemType } from '../../types';
import { useGlobalSearchModal } from './useGlobalSearchModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const SKELETON_ROWS = [0, 1, 2, 3];
const SKELETON_ROW_HEIGHT = 48;

function getItemIcon(type: SearchItemType): ReactNode {
  switch (type) {
    case 'sub_topic':
      return <TopicIcon />;
    case 'provider':
      return <BusinessIcon />;
    case 'document':
      return <DocumentIcon />;
    case 'meeting':
      return <CalendarIcon />;
  }
}

export default function GlobalSearchModal(props: Props) {
  const hook = useGlobalSearchModal(props);

  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onClose}
      fullWidth
      maxWidth="sm"
      title="Rechercher dans la copropriété"
      closeLabel="Fermer la recherche"
    >
      <Stack spacing={2}>
        <TextField
          id="global-search-input"
          inputRef={hook.inputRef}
          fullWidth
          autoFocus
          label="Sujet, prestataire, document ou réunion"
          value={hook.searchTerm}
          onChange={hook.handleSearchTermChange}
          onKeyDown={hook.handleKeyDown}
          slotProps={{
            htmlInput: {
              role: 'combobox',
              'aria-autocomplete': 'list',
              'aria-expanded': hook.hasResults,
              'aria-controls': hook.hasResults ? hook.listboxId : undefined,
              'aria-activedescendant': hook.activeOptionId,
              enterKeyHint: 'search',
              autoComplete: 'off',
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: hook.searchTerm ? (
                <InputAdornment position="end">
                  <IconButton tooltip="Effacer la recherche" edge="end" onClick={hook.handleClear}>
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />

        {hook.view === 'idle' ? (
          <EmptyState
            title="Que cherchez-vous ?"
            subtitle="Tapez au moins 2 lettres pour chercher parmi les sujets, prestataires, documents et réunions de la copropriété."
          />
        ) : null}

        {hook.view === 'loading' ? (
          <Stack spacing={1} aria-hidden>
            {SKELETON_ROWS.map((row) => (
              <Skeleton key={row} variant="rounded" height={SKELETON_ROW_HEIGHT} />
            ))}
          </Stack>
        ) : null}

        {hook.view === 'error' ? (
          <Alert
            severity="error"
            action={<Button variant="text" size="small" color="inherit" onClick={hook.handleRetry}>Réessayer</Button>}
          >
            La recherche n'a pas abouti. Vérifiez votre connexion puis réessayez.
          </Alert>
        ) : null}

        {hook.view === 'empty' ? (
          <EmptyState
            title="Aucun résultat"
            subtitle={`Aucun élément ne correspond à « ${hook.debouncedSearch} ». Vérifiez l'orthographe ou essayez un autre mot.`}
            action={<Button variant="text" onClick={hook.handleClear}>Effacer la recherche</Button>}
          />
        ) : null}

        {hook.view === 'results' ? (
          <List id={hook.listboxId} role="listbox" aria-label="Résultats de la recherche" disablePadding>
            {hook.groups.map((group) => (
              <li key={group.type} role="presentation">
                <List role="group" aria-labelledby={`${hook.listboxId}-${group.type}`} disablePadding>
                  <ListSubheader id={`${hook.listboxId}-${group.type}`} role="presentation" disableGutters>
                    {group.label}
                  </ListSubheader>
                  {group.items.map((entry) => (
                    <ListItemButton
                      key={`${entry.item.type}-${entry.item.id}`}
                      id={entry.optionId}
                      role="option"
                      aria-selected={entry.isActive}
                      selected={entry.isActive}
                      onClick={() => hook.handleSelect(entry.item)}
                      onMouseMove={() => hook.handleHover(entry.index)}
                    >
                      <ListItemIcon>{getItemIcon(entry.item.type)}</ListItemIcon>
                      <ListItemText primary={entry.item.title} secondary={entry.item.subtitle} />
                      {entry.item.badge ? <Tag color="done" variantIcon="state" label={entry.item.badge} /> : null}
                      {entry.isExternal ? <OpenInNewIcon fontSize="small" color="action" /> : null}
                    </ListItemButton>
                  ))}
                </List>
              </li>
            ))}
          </List>
        ) : null}

        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
          {hook.showKeyboardHint ? (
            <Typography variant="caption">
              Flèches haut et bas pour parcourir, Entrée pour ouvrir, Échap pour fermer.
            </Typography>
          ) : null}
          <Typography variant="caption" role="status">
            {hook.statusMessage}
          </Typography>
        </Stack>
      </Stack>
    </Dialog>
  );
}
